// src/Views/RingmasterPage.js — Ringmaster product landing page
import React from 'react';
import { Helmet } from 'react-helmet';

const styles = {
  page: {
    backgroundColor: 'var(--color-bg-canvas)',
    color: 'var(--color-text-on-dark)',
    minHeight: '100vh',
  },
  hero: {
    padding: 'calc(var(--space-16) + 60px) var(--space-6) var(--space-12)',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
  },
  badge: {
    display: 'inline-block',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-warning)',
    border: '1px solid var(--color-warning)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 10px',
    marginBottom: 'var(--space-4)',
    letterSpacing: 'var(--letter-spacing-label)',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 'var(--font-size-4xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-4)',
    lineHeight: 'var(--line-height-tight)',
  },
  tagline: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    maxWidth: '600px',
    margin: '0 auto',
  },
  section: {
    padding: '0 var(--space-6) var(--space-12)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-4)',
    lineHeight: 'var(--line-height-tight)',
  },
  paragraph: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-on-dark-body)',
    lineHeight: 'var(--line-height-relaxed)',
    marginBottom: 'var(--space-4)',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 'var(--space-4)',
    marginTop: 'var(--space-6)',
  },
  featureCard: {
    backgroundColor: 'var(--color-bg-surface-2)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-5)',
    border: '1px solid var(--color-border-subtle)',
  },
  featureTitle: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-2)',
  },
  featureDesc: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    margin: 0,
  },
  // Frostfire preview section
  frostfireSection: {
    margin: '0 var(--space-6) var(--space-12)',
    maxWidth: '800px',
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: '#0D001A',
    borderRadius: 'var(--radius-lg)',
    border: '2px solid #A066FF',
    padding: 'var(--space-8)',
    position: 'relative',
    overflow: 'hidden',
  },
  frostfireOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(160, 102, 255, 0.03) 2px, rgba(160, 102, 255, 0.03) 4px)',
    pointerEvents: 'none',
  },
  frostfireHeader: {
    fontFamily: "'IBM Plex Mono', 'Fira Code', monospace",
    fontSize: 'var(--font-size-xs)',
    color: '#A066FF',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: 'var(--space-4)',
    position: 'relative',
    zIndex: 1,
  },
  frostfireContent: {
    position: 'relative',
    zIndex: 1,
  },
  frostfireRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--space-2) 0',
    borderBottom: '1px solid rgba(160, 102, 255, 0.15)',
  },
  frostfireLabel: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 'var(--font-size-sm)',
    color: '#C799FF',
  },
  frostfireValue: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 'var(--font-size-sm)',
  },
  frostfireCaption: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 'var(--font-size-xs)',
    color: 'rgba(160, 102, 255, 0.6)',
    marginTop: 'var(--space-4)',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  },
  statusSection: {
    padding: 'var(--space-8) var(--space-6)',
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: 'var(--color-bg-surface)',
    borderRadius: 'var(--radius-lg)',
    marginBottom: 'var(--space-12)',
  },
  statusWrapper: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 var(--space-6) var(--space-12)',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-3)',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginTop: '7px',
    flexShrink: 0,
  },
  statusText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    margin: 0,
  },
  ctaSection: {
    padding: 'var(--space-12) var(--space-6) var(--space-16)',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
    borderTop: '1px solid var(--color-border-subtle)',
  },
  ctaText: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    marginBottom: 'var(--space-6)',
  },
  ctaLink: {
    display: 'inline-block',
    padding: 'var(--space-3) var(--space-8)',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-dark)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    textDecoration: 'none',
    transition: 'background-color var(--transition-base), box-shadow var(--transition-base)',
  },
};

const RingmasterPage = () => {
  return (
    <div style={styles.page}>
      <Helmet>
        <title>Ringmaster — Solomon Foshko | Design by Dialogue</title>
        <meta name="description" content="A tactical viewport for AI agent operations. See what your agents are doing, what needs approval, and what just shipped. Observation only — one-way data flow by design." />
      </Helmet>

      {/* Hero */}
      <div style={styles.hero}>
        <span style={styles.badge}>In Progress</span>
        <h1 style={styles.title}>Ringmaster</h1>
        <p style={styles.tagline}>
          A tactical viewport for AI agent operations — see what your agents are doing,
          what needs approval, and what just shipped.
        </p>
      </div>

      {/* What It Shows */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>What It Shows</h2>
        <p style={styles.paragraph}>
          Ringmaster is mission control for agent-run operations. Not a developer dashboard —
          an operator's viewport. It answers the questions that matter when AI agents are doing
          real work: Did the content publish? Is the data pipeline running? What needs my sign-off?
        </p>
        <div style={styles.featuresGrid}>
          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>Tactical Map</h3>
            <p style={styles.featureDesc}>
              See agents moving through workflows in real time. Which nodes are active, which
              are complete, where things stalled.
            </p>
          </div>
          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>Approval Queue</h3>
            <p style={styles.featureDesc}>
              Content, deployments, and decisions that need your sign-off — surfaced
              before they block the pipeline.
            </p>
          </div>
          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>Live Events</h3>
            <p style={styles.featureDesc}>
              A scrolling feed of operational events. Signal detected, draft written,
              workflow completed, error encountered.
            </p>
          </div>
          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>One-Way Data Flow</h3>
            <p style={styles.featureDesc}>
              Ringmaster watches. It never controls. Data flows in one direction — from
              agent systems into the viewport. Observation only, by design.
            </p>
          </div>
        </div>
      </div>

      {/* Frostfire Design Preview */}
      <div style={styles.frostfireSection}>
        <div style={styles.frostfireOverlay} />
        <div style={styles.frostfireHeader}>
          {'// MISSION BRIEFING — PREVIEW'}
        </div>
        <div style={styles.frostfireContent}>
          <div style={styles.frostfireRow}>
            <span style={styles.frostfireLabel}>WORKFLOW</span>
            <span style={{...styles.frostfireValue, color: '#C799FF'}}>Content Pipeline — Daily</span>
          </div>
          <div style={styles.frostfireRow}>
            <span style={styles.frostfireLabel}>STATUS</span>
            <span style={{...styles.frostfireValue, color: '#00FFCC'}}>AWAITING APPROVAL</span>
          </div>
          <div style={styles.frostfireRow}>
            <span style={styles.frostfireLabel}>AGENTS</span>
            <span style={{...styles.frostfireValue, color: '#66FF99'}}>Signal ● Writer ● Observer</span>
          </div>
          <div style={styles.frostfireRow}>
            <span style={styles.frostfireLabel}>CYCLE</span>
            <span style={{...styles.frostfireValue, color: '#C799FF'}}>3/5 — Draft reviewed, pending operator</span>
          </div>
          <div style={{...styles.frostfireRow, borderBottom: 'none'}}>
            <span style={styles.frostfireLabel}>LAST EVENT</span>
            <span style={{...styles.frostfireValue, color: '#7CCBFF'}}>Observer flagged tone — 2.3s ago</span>
          </div>
        </div>
        <p style={styles.frostfireCaption}>
          Frostfire design system — dark tactical theme with accent lighting
        </p>
      </div>

      {/* Current Status */}
      <div style={styles.statusWrapper}>
        <div style={styles.statusSection}>
          <h2 style={{...styles.sectionTitle, marginBottom: 'var(--space-4)'}}>Current Status</h2>
          <p style={{...styles.paragraph, marginBottom: 'var(--space-4)'}}>
            Ringmaster is earlier-stage than WorkAround. Here's the honest picture:
          </p>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-success)'}} />
            <p style={styles.statusText}>React component with tactical map, agent office, live events, and approval queue</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-success)'}} />
            <p style={styles.statusText}>Full Frostfire dark theme implementation</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-success)'}} />
            <p style={styles.statusText}>Demonstrated with SteamCharts workflow using demo data</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-warning)'}} />
            <p style={styles.statusText}>No real agent event feed — currently renders demo data only</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-text-on-dark-muted)'}} />
            <p style={styles.statusText}>Not yet a standalone application — React component in development</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-text-on-dark-muted)'}} />
            <p style={styles.statusText}>No integration with any agent framework yet</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={styles.ctaSection}>
        <p style={styles.ctaText}>
          Ringmaster is a bet that agent-run businesses need their own observability layer —
          not a developer trace viewer, but an operator's viewport. Follow the build to see
          if that bet pays off.
        </p>
        <a
          href="https://www.linkedin.com/in/solomonfoshko/"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.ctaLink}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'var(--color-primary-dark)';
            e.target.style.boxShadow = 'var(--shadow-dark-button)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'var(--color-primary)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Follow the build on LinkedIn
        </a>
      </div>
    </div>
  );
};

export default RingmasterPage;
